import React, { useCallback, useEffect, useState } from "react";
import { Card as RawCard } from "@/components/ui/card";
import { Badge as RawBadge } from "@/components/ui/badge";
import {
  Calendar,
  CalendarPlus,
  Flag,
  MapPin,
  Eye,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { format } from "date-fns";
import { parseDateOnly } from "@/utils/parseDateOnly";
import { auth } from "../../firebase";
import { getUserBookings } from "@/services/bookingService";
import BookingDetailsDialog from "./BookingDetailsDialog";

const PAGE_SIZE = 6;

function toJsDate(value) {
  if (!value) return null;
  const d = parseDateOnly(value);
  return isNaN(d.getTime()) ? null : d;
}

function formatDate(value) {
  const d = toJsDate(value);
  if (!d) return null;
  return format(d, "dd MMM yyyy");
}

export default function BookingHistorySection() {
  const Card = /** @type {React.ComponentType<any>} */ (RawCard);
  const Badge = /** @type {React.ComponentType<any>} */ (RawBadge);

  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);

  // Only paid bookings represent an actual confirmed getaway worth showing
  // in history — pending/unpaid drafts are filtered out client-side since
  // there's no composite Firestore index for (userId, paymentStatus, createdAtServer).
  const paidBookings = bookings.filter((b) => b.paymentStatus === "paid");

  const loadFirstPage = useCallback(async () => {
    const uid = auth.currentUser?.uid;
    if (!uid) {
      setLoading(false);
      setBookings([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const {
        bookings: page,
        nextCursor,
        hasMore: more,
      } = await getUserBookings(uid, {
        pageSize: PAGE_SIZE,
      });
      setBookings(page);
      setCursor(nextCursor);
      setHasMore(more);
    } catch (err) {
      console.error("Failed to load bookings:", err);
      setError(err?.message || "Failed to load your bookings.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFirstPage();
  }, [loadFirstPage]);

  const loadMore = async () => {
    const uid = auth.currentUser?.uid;
    if (!uid || !cursor) return;
    setLoadingMore(true);
    try {
      const {
        bookings: page,
        nextCursor,
        hasMore: more,
      } = await getUserBookings(uid, {
        pageSize: PAGE_SIZE,
        cursor,
      });
      setBookings((prev) => [...prev, ...page]);
      setCursor(nextCursor);
      setHasMore(more);
    } catch (err) {
      console.error("Failed to load more bookings:", err);
      setError(err?.message || "Failed to load more bookings.");
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card className="p-6 bg-white border border-gray-100">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-[#0B2421]">Recent Bookings</h2>
        </div>

        {loading && (
          <div className="flex items-center justify-center gap-2 py-12 text-gray-400">
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading your bookings...
          </div>
        )}

        {!loading && error && (
          <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {!loading && !error && !auth.currentUser && (
          <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-lg">
            <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Sign in to see your bookings
            </h3>
            <p className="text-gray-600">
              Your booking history will appear here once you're signed in.
            </p>
          </div>
        )}

        {!loading && !error && auth.currentUser && paidBookings.length === 0 && (
          <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-lg">
            <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No bookings yet
            </h3>
            <p className="text-gray-600">
              Paid getaways will appear here once confirmed.
            </p>
          </div>
        )}

        {!loading && !error && paidBookings.length > 0 && (
          <>
            <div className="space-y-4">
              <AnimatePresence initial={false}>
                {paidBookings.map((booking) => {
                  const rounds =
                    booking.selectedPackage?.rounds ??
                    booking.selectedCourses?.length ??
                    0;
                  const startDate =
                    booking.startDate || booking.roundDates?.[0];
                  const endDate =
                    booking.endDate ||
                    booking.roundDates?.[booking.roundDates?.length - 1];

                  // "Getaway" date used to decide upcoming vs. completed —
                  // the last day of the trip if there is a range, else the
                  // single getaway date.
                  const getawayRefDate = toJsDate(endDate) || toJsDate(startDate);
                  const startOfToday = new Date();
                  startOfToday.setHours(0, 0, 0, 0);
                  const isUpcoming = getawayRefDate
                    ? getawayRefDate >= startOfToday
                    : true;

                  const primaryCourse = booking.selectedCourses?.[0];
                  const title =
                    booking.selectedCourses?.length === 1
                      ? primaryCourse?.name || "Golf Getaway"
                      : `${rounds} Round Golf Getaway`;
                  const destination =
                    [primaryCourse?.city, primaryCourse?.country]
                      .filter(Boolean)
                      .join(", ") ||
                    booking.selectedAccommodation?.destination ||
                    "";

                  return (
                    <motion.div
                      key={booking.id}
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className="rounded-2xl border border-gray-200 bg-white p-5 flex flex-col sm:flex-row items-start justify-between gap-4 hover:shadow-md transition-shadow min-w-0"
                    >
                      <div className="flex-1 min-w-0">
                        {/* Header */}
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 shrink-0 rounded-full border-2 border-[#0B2421]/20 flex items-center justify-center bg-[#0B2421]/6">
                            <Flag className="h-5 w-5 text-[#0B2421]" />
                          </div>
                          <div className="min-w-0">
                            <h3 className="text-base font-bold text-[#0B2421] truncate">
                              {title}
                            </h3>
                            {destination && (
                              <div className="flex items-center gap-1 text-xs text-gray-500">
                                <MapPin className="h-3 w-3 shrink-0" />
                                <span className="truncate">{destination}</span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Info grid */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                          <div className="flex items-center gap-2 text-sm">
                            <CalendarPlus className="h-4 w-4 text-gray-400 shrink-0" />
                            <div className="min-w-0">
                              <div className="text-gray-400 text-xs">Booked</div>
                              <div className="font-semibold text-[#0B2421] text-sm truncate">
                                {formatDate(booking.createdAtServer) || "—"}
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 text-sm col-span-2 md:col-span-1">
                            <Calendar className="h-4 w-4 text-gray-400 shrink-0" />
                            <div className="min-w-0">
                              <div className="text-gray-400 text-xs">Getaway</div>
                              <div className="font-semibold text-[#0B2421] text-sm">
                                {startDate ? formatDate(startDate) : "—"}
                                {endDate && endDate !== startDate
                                  ? ` – ${formatDate(endDate)}`
                                  : ""}
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 text-sm">
                            <Flag className="h-4 w-4 text-gray-400 shrink-0" />
                            <div>
                              <div className="text-gray-400 text-xs">Rounds</div>
                              <div className="font-semibold text-[#0B2421] text-sm">
                                {rounds}
                              </div>
                            </div>
                          </div>

                          <div className="text-sm">
                            <div className="text-gray-400 text-xs">Total</div>
                            <div className="font-bold text-[#D4AF37] text-lg">
                              SGD {booking.grandTotal}
                            </div>
                          </div>
                        </div>

                        {/* Status + reference */}
                        <div className="flex items-center gap-3 flex-wrap">
                          <Badge
                            className={
                              isUpcoming
                                ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                                : "bg-gray-100 text-gray-600 border-gray-200"
                            }
                          >
                            {isUpcoming
                              ? "Getaway Upcoming"
                              : "Getaway Completed"}
                          </Badge>
                          <span className="text-xs text-gray-400">
                            Ref: {booking.reservationNumber || "—"}
                          </span>
                        </div>
                      </div>

                      {/* Action */}
                      <div className="flex sm:flex-col flex-row gap-2 w-full sm:w-auto sm:min-w-[110px] min-w-0">
                        <button
                          type="button"
                          onClick={() => setSelectedBooking(booking)}
                          className="flex-1 sm:flex-none min-w-0 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#0B2421] hover:bg-[#0B2421]/85 text-white whitespace-nowrap"
                        >
                          <Eye className="h-3.5 w-3.5 shrink-0" /> View Details
                        </button>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>

            {hasMore && (
              <div className="flex justify-center mt-6">
                <button
                  type="button"
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="h-11 px-6 rounded-xl border border-[#0B2421]/20 text-[#0B2421] font-semibold hover:bg-[#0B2421]/5 transition-colors disabled:opacity-40 flex items-center gap-2"
                >
                  {loadingMore ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    "Load more"
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </Card>

      <BookingDetailsDialog
        open={!!selectedBooking}
        onOpenChange={(open) => !open && setSelectedBooking(null)}
        booking={selectedBooking}
      />
    </motion.div>
  );
}
